import {
	AspectMaintainer, Checkbox, PopoverPlacement, RangeSlider, Select, Switch
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { Dispatch, SetStateAction } from "react";
import { Fragment, memo, useEffect, useState } from "react";
import "Components/ConfigLayout/Camera/OverlaySettings/BorderOverlaySettings/BorderOverlaySettings.less";
import { borderColorOptions, displayModeOptions, timeOptions } from "Data/Objects/BannerOverlay";
import type { BannerDisplayMode, BannerEventTypes, BorderColor, OverlayState } from "Interfaces";
import { addOrRemoveFromArray } from "@clintonelec/typescriptutils";

type BorderOverlaySettingsProps = {
	localOverlaySettings: OverlayState;
	setLocalOverlaySettings: Dispatch<SetStateAction<OverlayState>>;
};

function BorderOverlaySettings(props: BorderOverlaySettingsProps) {
	const { localOverlaySettings, setLocalOverlaySettings } = props;
	const { borderOverlay, enableOverlays: mainToggle } = localOverlaySettings;
	const {
		enableOverlay, borderColor, borderWeight, enableFlash, flashOn, flashOff, displayMode, eventTypes
	} = borderOverlay;

	const [ hiddenBorder, setHiddenBorder ] = useState(false);
	const hiddenClass = (hiddenBorder || !mainToggle || !enableOverlay) ? "hidden" : null;
	const weightClass = `border-${ borderWeight }`;
	const colorClass = `border-${ borderColor.toLowerCase() }`;
	const compositeClass = [ "preview-wrapper border", weightClass, colorClass, hiddenClass ].join(" ");

	useEffect(() => {
		if (!enableFlash) {
			return;
		}

		let timeout;

		if (hiddenBorder) {
			timeout = setTimeout(() => setHiddenBorder(false), flashOff * 1000);
		} else {
			timeout = setTimeout(() => setHiddenBorder(true), flashOn * 1000);
		}

		return () => {
			clearTimeout(timeout);
		};
	}, [ hiddenBorder, flashOff, flashOn, enableFlash ]);

	const handleEnableOverlayChange = (newEnableOverlay: boolean) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.borderOverlay.enableOverlay = newEnableOverlay;
		}));
	};

	const handleEnableFlashChange = (newEnableFlash: boolean) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.borderOverlay.enableFlash = newEnableFlash;
		}));

		setHiddenBorder(false);
	};

	const handleFlashOnSelect = (newFlashOn: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.borderOverlay.flashOn = Number(newFlashOn);
		}));
	};

	const handleFlashOffSelect = (newFlashOff: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.borderOverlay.flashOff = Number(newFlashOff);
		}));
	};

	const handleDisplayModeSelect = (newDisplayMode: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.borderOverlay.displayMode = newDisplayMode as BannerDisplayMode;
		}));
	};

	const handleBorderColorSelect = (color: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.borderOverlay.borderColor = color as BorderColor;
		}));
	};

	const handleBorderWeightChange = (newBorderWeight: number | number[]) => {
		setLocalOverlaySettings(produce(localOverlaySettings, (draft) => {
			if (Array.isArray(newBorderWeight)) {
				return;
			}

			draft.borderOverlay.borderWeight = newBorderWeight;
		}));
	};

	const handleEventTypesChange = (eventType: BannerEventTypes) => () => {
		const updatedEventTypes = addOrRemoveFromArray<BannerEventTypes>(Array.from(eventTypes), eventType);

		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.borderOverlay.eventTypes = new Set(updatedEventTypes);
		}));
	};

	const eventWarning = eventTypes.size < 1
		? <div className="form-row event-type-warning inset">You must enable at least one event type</div>
		: null;

	const renderBorderSettings = () => {
		if (!enableOverlay) {
			return <div className="enable-message">Enable Border to adjust settings.</div>;
		}

		return (
			<div>
				<div className="form-row">
					<span>Border Color</span>
					<Select
						className="form-select"
						allowClear={ false }
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleBorderColorSelect }
						options={ borderColorOptions }
						value={ borderColor }
					/>
				</div>
				<div className="form-row">
					<span>Border Weight</span>
					<div className="slider-container">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							min={ 1 }
							max={ 5 }
							onChange={ handleBorderWeightChange }
							value={ borderWeight }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Display Mode</span>
					<Select
						className="form-select"
						allowClear={ false }
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleDisplayModeSelect }
						options={ displayModeOptions }
						value={ displayMode }
					/>
				</div>
				<div className="form-row">
					<span><span className="inset">Event Type</span></span>
					<div className="event-types">
						<Checkbox
							checked={ eventTypes.has("Motion") }
							onChange={ handleEventTypesChange("Motion") }
							disabled={ displayMode !== "Event" }
						>
							<span>Motion</span>
						</Checkbox>
						<Checkbox
							checked={ eventTypes.has("Analytics") }
							onChange={ handleEventTypesChange("Analytics") }
							disabled={ displayMode !== "Event" }
						>
							<span>Analytics</span>
						</Checkbox>
						<Checkbox
							checked={ eventTypes.has("Alarm In") }
							onChange={ handleEventTypesChange("Alarm In") }
							disabled={ displayMode !== "Event" }
						>
							<span>Alarm In</span>
						</Checkbox>
					</div>
				</div>
				{ eventWarning }
				<div className="form-row">
					<span>Flash</span>
					<div className="form-switch-wrapper">
						<Switch
							checked={ enableFlash }
							onChange={ handleEnableFlashChange }
						/>
					</div>
				</div>
				<div className="form-row">
					<span><span className="inset">Flash On Duration</span></span>
					<Select
						className="form-select"
						allowClear={ false }
						disabled={ !enableOverlay || !enableFlash }
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleFlashOnSelect }
						options={ timeOptions }
						value={ flashOn }
					/>
				</div>
				<div className="form-row">
					<span><span className="inset">Flash Off Duration</span></span>
					<Select
						className="form-select"
						allowClear={ false }
						disabled={ !enableOverlay || !enableFlash }
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleFlashOffSelect }
						options={ timeOptions }
						value={ flashOff }
					/>
				</div>
			</div>
		);
	};

	const renderBorderControls = () => {
		if (!mainToggle) {
			return <div className="enable-message">Enable Overlays to adjust settings.</div>;
		}

		return (
			<Fragment>
				<div className="form-row">
					<Switch
						className="enable-overlay-switch"
						checked={ enableOverlay }
						onChange={ handleEnableOverlayChange }
					/>
					<span>Enable Border</span>
				</div>
				{ renderBorderSettings() }
			</Fragment>
		);
	};

	return (
		<div className="border-overlay-settings columns">
			<div className="column">
				<AspectMaintainer>
					<div className="live-snapshot">
						<AspectMaintainer>
							<div className={ compositeClass }>
								<div className="camera-snapshot" />
							</div>
						</AspectMaintainer>
					</div>
				</AspectMaintainer>
			</div>
			<div className="column">
				{ renderBorderControls() }
			</div>
		</div>
	);
}

export default memo(BorderOverlaySettings);
